import React, { useState, useEffect } from "react";
import Layout from "../../components/layouts/layout";
import BannerBgImage from "../../assets/Images/services/consult-banner.png";
import TwoColumnContactInfo from "../../templates/TwoColumnContactInfo";
import Clm1Img from "../../assets/Icons/phone.svg";
import Clm2Img from "../../assets/Icons/mail.svg";
import BannerSection from "../../components/servicesSection/banner";
import OurOffering from "../../components/servicesSection/ourOffering";
import CtaCardSection from "../../components/CTASection/ctaCard2";
import CTAIMAGE1 from "../../assets/Images/Clutch/Cta/phone-contact.png";
import FullStackExpertise from "../../components/FullStackExpertise";
import ConsultImage from "../../assets/Images/Homepage/FullStackExpertise/consult.png";
import OurModelSection from "../../components/OurModelSection";
import SkeletonOfferingLoader from "../../components/SkeletonOfferingCard";
import FAQSection from "../../components/NewHomePage/FAQSection";

const consultFaqs = [
  {
    q: "What is technology consulting and how can it help my business?",
    a:
      "Technology consulting involves advising businesses on how to use technology effectively to achieve their goals. It helps organisations identify inefficiencies, reduce risks, choose the right tech stack, and build scalable, future-ready systems that support long-term growth.",
  },
  {
    q: "What does DigiRyte's consulting service include?",
    a:
      "DigiRyte's consulting services include technology audits, architecture advisory, digital transformation strategy, AI strategy, and scalability planning. We provide actionable insights, prioritised recommendations, and a clear roadmap to help businesses make informed technology decisions.",
  },
  {
    q: "When should a company engage a technology consulting partner?",
    a:
      "A company should engage a consulting partner when planning a new product, scaling an existing system, modernising legacy infrastructure, or facing performance and reliability issues. Early engagement helps prevent costly mistakes and ensures a strong foundation.",
  },
  {
    q:
      "How does consulting help organisations plan successful digital transformation?",
    a:
      "Consulting provides a structured approach to digital transformation by assessing current systems, identifying gaps, and defining a clear strategy. It ensures that technology investments align with business goals and are implemented in a scalable and sustainable way.",
  },
  {
    q:
      "What is the difference between technology consulting and software development?",
    a:
      "Technology consulting focuses on strategy, planning, and decision-making, while software development involves building and implementing solutions. Consulting ensures the right approach is defined before development begins, reducing risks and improving outcomes.",
  },
  {
    q:
      "Do you provide digital transformation consulting for enterprises and startups?",
    a:
      "Yes, DigiRyte works with both startups and enterprises. Our consulting approach is tailored to the organisation's stage of growth, ensuring practical, scalable solutions whether you're building from scratch or transforming existing systems.",
  },
  {
    q:
      "How do consulting services help reduce technology risks before development?",
    a:
      "Consulting identifies potential risks such as poor architecture, scalability limitations, security vulnerabilities, and technical debt. By addressing these early, businesses can avoid costly rework, delays, and performance issues later in the development cycle.",
  },
  {
    q:
      "Can DigiRyte help define a technology strategy for scaling a digital product?",
    a:
      "Absolutely. DigiRyte helps businesses design a robust technology strategy that supports rapid growth, optimises performance, and ensures systems can handle increased demand without compromising reliability.",
  },
  {
    q:
      "What role does architecture advisory play in digital transformation projects?",
    a:
      "Architecture advisory ensures that systems are designed with scalability, flexibility, and performance in mind. It provides a strong technical foundation that enables seamless integration, efficient operations, and long-term sustainability.",
  },
  {
    q:
      "How does DigiRyte assess business requirements before recommending solutions?",
    a:
      "DigiRyte conducts in-depth discovery sessions, stakeholder interviews, and system analysis to understand business objectives, challenges, and constraints. This ensures that recommendations are practical, relevant, and aligned with strategic goals.",
  },
  {
    q:
      "Do your consulting services include AI and emerging technology strategy?",
    a:
      "Yes, DigiRyte provides guidance on AI and emerging technologies, helping businesses identify relevant use cases, assess feasibility, and integrate intelligent solutions that drive innovation and competitive advantage.",
  },
  {
    q: "How do consulting engagements typically start with DigiRyte?",
    a:
      "Engagements usually begin with an initial consultation to understand your challenges and goals. This is followed by a detailed assessment, after which we provide a tailored roadmap with clear, actionable recommendations.",
  },
  {
    q: "How long does a typical consulting engagement last?",
    a:
      "The duration depends on the scope and complexity of the project. Some engagements, such as audits, may take a few weeks, while broader transformation initiatives can extend over several months.",
  },
  {
    q:
      "What outcomes can businesses expect from a technology consulting engagement?",
    a:
      "Businesses can expect improved clarity, reduced risk, optimised systems, and a well-defined roadmap for growth. The outcome is a stronger technology foundation that supports scalability, efficiency, and long-term success.",
  },
  {
    q: "Why should organisations choose DigiRyte for technology consulting?",
    a:
      "DigiRyte combines deep technical expertise with a practical, business-focused approach. We don't just provide recommendations — we deliver actionable insights and support implementation, ensuring real, measurable results.",
  },
];

function ConsultScreen() {
  // Import Sanity service inside component to avoid build-time issues
  const { SanityService } = require("../../services/sanity");

  // State for dynamic offerings
  const [offerList, setOfferList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch offerings from Sanity on component mount
  useEffect(() => {
    const fetchOfferings = async () => {
      try {
        setLoading(true);
        setError(null);
        const sanityOfferings = await SanityService.getOfferingsByPageType(
          "consult",
        );
        setOfferList(sanityOfferings || []);
      } catch (error) {
        console.error("Error fetching offerings:", error);
        setError("Failed to load offerings. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchOfferings();
  }, []);

  const TwoColumnContactInfoData = {
    Clm1Img: Clm1Img,
    Clm1Title: "Phone:",
    Clm1SubTitle: "+44 (0) 161 706 7983",
    Clm2Img: Clm2Img,
    Clm2Title: "Email:",
    Clm2SubTitle: "hello@digiryte.com",
  };

  return (
    <Layout
      seo={{
        title: "Service - Consult | DigiRyte",
        ogtitle: "Service - Consult | DigiRyte",
        description: "Consult",
        keywords:
          "Product Stratergy, CX Strategy, AI & ML Strategy, Application Modernisation, AWS Solutions, AI-Driven SDLC, Data Architecture, Disaster Recovery & Business Continuity Planning, Digital Transformation, Product Scaling, Agile Transformation, IT Risk & Assurance",
      }}
    >
      <BannerSection
        bannerImage={BannerBgImage}
        title="Consult"
        subtitle={<>Strategic Digital Planning</>}
        image={ConsultImage}
      />

      <div className="mt-40">
        <div className="body-container">
          {loading ? (
            <SkeletonOfferingLoader count={6} />
          ) : error ? (
            <div
              style={{
                textAlign: "center",
                padding: "100px 20px",
                minHeight: "400px",
              }}
            >
              <p
                style={{
                  fontSize: "18px",
                  color: "#D32F2F",
                  marginBottom: "10px",
                }}
              >
                {error}
              </p>
              <p style={{ fontSize: "14px", color: "#666" }}>
                Please refresh the page or contact support.
              </p>
            </div>
          ) : (
            <OurOffering
              title="Our Offerings"
              details="We work with you to define your strategic vision, aligning your goals with tailored digital roadmaps, robust technical architecture and impactful planning to drive business transformation."
              CardListData={offerList}
              pageName="consult"
            />
          )}
        </div>
      </div>
      <div className="mt-70 common-o-bg">
        <CtaCardSection
          className=""
          title={
            <>
              <span className="animationTitle">Ready to </span>
              partner with us?
            </>
          }
          imageUrl={CTAIMAGE1}
          imgClassName="custom-cta-img custom-cta-darken"
        />
      </div>
      <div className="mt-50">
        <FullStackExpertise
          parentWarp="services-modal-wrap"
          title="Other Services"
          showButton={false}
          showItems={["Audit", "Build"]}
          isServiceCard
        />
      </div>

      <div className="mt-30">
        <OurModelSection />
      </div>
      <div className="mt-70">
        <FAQSection faqs={consultFaqs} />
      </div>
      <div className="mt-70">
        {/* <Box BoxData={BoxData} /> */}
        <TwoColumnContactInfo
          TwoColumnContactInfoData={TwoColumnContactInfoData}
        />
      </div>
    </Layout>
  );
}

export default ConsultScreen;
